import { AppRoutes } from "@/constants/routes";
import {
  getFertSlotRequestOrdersDetail,
  updateFertSlotRequestOrders,
  UpdatePedidosSolicitudBody,
} from "@/services/fert-slot-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import { NavigateFunction } from "react-router-dom";
import { UseFormReset } from "react-hook-form";
import { FuelSlotRequestOrderEditForm } from "./fuel-slot-request-order-edit.schema";

export interface FuelSlotOrderProductDto {
  id?: number;
  nombreProducto?: string;
  cantidad?: number | string;
}

export interface FuelSlotOrderDto {
  id?: number;
  estado?: string;
  intentos?: unknown[];
  chofer?: string | null;
  lugarDespacho?: string;
  nombreDespacho?: string;
  observaciones?: string;
  URLMaps?: string;
  urlMaps?: string;
  urlmaps?: string;
  productos?: FuelSlotOrderProductDto[];
}

export interface FuelSlotOrderFormProduct {
  productId: string[];
  tons: string;
}

export interface FuelSlotOrderFormItem {
  orderId: number;
  dispatchPlace: string[];
  dispatchName?: string;
  chofer?: string | null;
  observations: string;
  URLMaps?: string;
  products: FuelSlotOrderFormProduct[];
}

export interface UpdateOrdersHandlerProps {
  id: string | undefined;
  values: FuelSlotRequestOrderEditForm;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export interface OrderProduct {
  id: number;
  nombre: string;
}

export interface OrderMeta {
  orderId: number;
  estado?: string;
  hasAttempts?: boolean;
}

export interface FetchOrdersForEditHandlerProps {
  id: string | undefined;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoadingOrders: (value: boolean) => void;
  reset: UseFormReset<FuelSlotRequestOrderEditForm>;
  setOrderProducts: (products: OrderProduct[]) => void;
  setOrdersMeta?: (orders: OrderMeta[]) => void;
}

export const fetchOrdersForEditHandler = async ({
  id,
  navigate,
  fetchPermissions,
  setLoadingOrders,
  reset,
  setOrderProducts,
  setOrdersMeta,
}: FetchOrdersForEditHandlerProps) => {
  try {
    setLoadingOrders(true);
    const response = await getFertSlotRequestOrdersDetail(Number(id));
    const orders: FuelSlotOrderDto[] = (response ?? []) as FuelSlotOrderDto[];

    setOrdersMeta?.(
      orders.map((o) => ({
        orderId: Number(o?.id ?? 0),
        estado: o?.estado,
        hasAttempts: (o?.intentos ?? []).length >= 1,
      })),
    );

    const productsFromOrders: OrderProduct[] = [];
    orders.forEach((order) => {
      (order?.productos ?? []).forEach((p) => {
        if (p?.id && p?.nombreProducto) {
          productsFromOrders.push({
            id: p.id,
            nombre: p.nombreProducto,
          });
        }
      });
    });
    setOrderProducts(productsFromOrders);

    const formOrders: FuelSlotOrderFormItem[] = orders.map((order) => ({
      orderId: Number(order?.id ?? 0),
      dispatchPlace: order?.lugarDespacho ? [String(order.lugarDespacho)] : [],
      dispatchName: order?.nombreDespacho ?? "-",
      chofer: order?.chofer ?? null,
      observations: String(order?.observaciones ?? ""),
      URLMaps: String(order?.URLMaps ?? order?.urlMaps ?? order?.urlmaps ?? ""),
      products: (order?.productos ?? []).map((p) => ({
        productId: p?.id ? [String(p.id)] : [],
        tons: String(p?.cantidad ?? ""),
      })),
    }));

    if (formOrders.length === 0) {
      formOrders.push({
        orderId: 0,
        dispatchPlace: [],
        observations: "",
        products: [{ productId: [], tons: "" }],
      } as any);
    }

    reset({ orders: formOrders } as any);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FUEL_SLOTS_REQ,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          "No se pudieron cargar los pedidos.",
        ),
      });
    }
  } finally {
    setLoadingOrders(false);
  }
};

export const updateOrdersHandler = async ({
  id,
  values,
  navigate,
  fetchPermissions,
}: UpdateOrdersHandlerProps) => {
  try {
    const normalizeSingle = (v: any) => {
      if (Array.isArray(v)) return v?.[0];
      return v;
    };

    const body: UpdatePedidosSolicitudBody = {
      pedidos: (values?.orders ?? []).map((o: any) => ({
        idPedido: Number(o?.orderId ?? 0),
        lugarDespacho: String(normalizeSingle(o?.dispatchPlace) ?? ""),
        observaciones: String(o?.observations ?? ""),
        ...(String(o?.URLMaps ?? "").trim()
          ? { URLMaps: String(o?.URLMaps ?? "").trim() }
          : {}),
        productos: (o?.products ?? []).map((p: any) => {
          const raw = typeof p?.tons === "string" ? p.tons : String(p?.tons ?? "");
          const trimmed = raw.trim();
          let parsed = 0;
          if (trimmed) {
            parsed = Number.parseFloat(trimmed.replace(",", "."));
          }

          const productIdRaw = normalizeSingle(p?.productId);
          const productId = Number(productIdRaw ?? 0);

          return {
            idProducto: Number.isFinite(productId) ? productId : 0,
            cantidad: Number.isFinite(parsed) ? parsed : 0,
          };
        }),
      })),
    };

    await updateFertSlotRequestOrders(Number(id), body);

    toaster.create({
      type: "success",
      title: "Pedidos actualizados exitosamente.",
    });

    navigate(AppRoutes.FUEL_SLOTS_REQ_DETAIL.replace(":id", String(id)));
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FUEL_SLOTS_REQ_DETAIL.replace(":id", String(id)),
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error,
          "No se pudieron actualizar los pedidos.",
        ),
      });
    }

    throw error;
  }
};
